/**
 * useAuthStore — 认证状态管理（Zustand）
 * - 管理用户信息、登录状态、加载状态
 * - 使用 persist 中间件将 user + isAuthenticated 持久化到 localStorage
 * - 提供 login / register / logout / fetchUser 方法
 * - 配合后端 HttpOnly Cookie 方案：Token 存储在 Cookie 中，前端仅保存用户信息
 */
import {create} from 'zustand'
import type {User} from '@/types'
import {authApi} from '@/api/auth'
import axios from 'axios'
import {persist} from 'zustand/middleware'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  isAdmin: boolean
  login: (username: string, password: string) => Promise<void>
  register: (username: string, email: string, password: string, nickname: string, emailCode: string) => Promise<void>
  logout: () => Promise<void>
  fetchUser: () => Promise<void>
  setUser: (user: User | null) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isAdmin: false,
      loading: false,

      /** 登录：调用 API 后更新用户状态 */
      login: async (username, password) => {
        set({loading: true})
        try {
          const res = await authApi.login({username, password})
          set({user: res.user, isAuthenticated: true, loading: false, isAdmin: res.user.roles.includes('admin')})
        } catch {
          set({loading: false})
          throw new Error('登录失败')
        }
      },

      /** 注册：注册成功后自动登录 */
      register: async (username, email, password, nickname, emailCode) => {
        set({loading: true})
        try {
          const res = await authApi.register({username, email, password, nickname, emailCode})
          set({user: res.user, isAuthenticated: true, loading: false, isAdmin: res.user.roles.includes('admin')})
        } catch {
          set({loading: false})
          throw new Error('注册失败')
        }
      },

      /** 登出：调用后端清除 Cookie，前端清空状态 */
      logout: async () => {
        try {
          await authApi.logout()
        } finally {
          set({user: null, isAuthenticated: false, isAdmin: false})
        }
      },

      /** 获取当前用户信息（用于页面刷新后恢复状态） */
      fetchUser: async () => {
        try {
          const user = await authApi.getMe()
          set({user, isAuthenticated: true, isAdmin: user.roles.includes('admin')})
        } catch (error) {
          if (isAuthExpired(error)) {
            set({user: null, isAuthenticated: false, isAdmin: false})
          }
          throw error
        }
      },

      setUser: (user) => set({user, isAuthenticated: !!user, isAdmin: user?.roles.includes('admin') ?? false})
    }),
    {
      name: 'auth-storage', // localStorage key
      // 仅持久化用户信息和登录状态，不持久化 loading
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isAdmin: state.isAdmin
      })
    }
  )
)

function isAuthExpired(error: unknown) {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 401
  }

  if (error && typeof error === 'object' && 'statusCode' in error) {
    return (error as {statusCode?: number}).statusCode === 401
  }

  return false
}
